import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from '../../../ui/Button';
import './GroundLoot.css';

function focusableElements(container) {
  if (!container) return [];
  return [...container.querySelectorAll(
    'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
  )].filter(element => !element.hasAttribute('hidden'));
}

export default function GroundLootDialogFrame({
  busy = false,
  children,
  className = '',
  description,
  eyebrow = 'Shared world treasure',
  getFallbackFocusTarget,
  onClose,
  shouldRestoreFocus,
  title,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const fallbackFocusRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    fallbackFocusRef.current = getFallbackFocusTarget?.(openerRef.current) ?? null;
    const appRoot = document.querySelector('.game-shell');
    const previousAriaHidden = appRoot?.getAttribute('aria-hidden');
    const wasInert = Boolean(appRoot?.inert);
    if (appRoot) {
      appRoot.inert = true;
      appRoot.setAttribute('aria-hidden', 'true');
    }

    const focusTimer = window.setTimeout(() => {
      focusableElements(dialogRef.current)[0]?.focus({ preventScroll: true });
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      if (appRoot) {
        appRoot.inert = wasInert;
        if (previousAriaHidden === null) appRoot.removeAttribute('aria-hidden');
        else appRoot.setAttribute('aria-hidden', previousAriaHidden);
      }
      const restoreOpener = shouldRestoreFocus?.() ?? true;
      const currentFallback = fallbackFocusRef.current?.isConnected
        ? fallbackFocusRef.current
        : getFallbackFocusTarget?.(openerRef.current);
      const focusTarget = restoreOpener && openerRef.current?.isConnected
        ? openerRef.current
        : currentFallback;
      if (focusTarget?.isConnected) {
        focusTarget.focus({ preventScroll: true });
      }
      if (getFallbackFocusTarget) {
        window.setTimeout(() => {
          const activeElement = document.activeElement;
          if (
            activeElement
            && activeElement !== document.body
            && activeElement.isConnected
          ) {
            return;
          }
          const latestFallback = getFallbackFocusTarget(openerRef.current);
          if (latestFallback?.isConnected) {
            latestFallback.focus({ preventScroll: true });
          }
        }, 0);
      }
    };
  }, [getFallbackFocusTarget, shouldRestoreFocus]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !busy) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [busy, onClose]);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(
    <div
      className="ground-loot-modal"
      onPointerDown={event => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        aria-busy={busy || undefined}
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`ground-loot-dialog ${className}`.trim()}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <header className="ground-loot-dialog__header">
          <div>
            <p className="ground-loot-dialog__eyebrow">{eyebrow}</p>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <Button
            aria-label={`Close ${title}`}
            className="ground-loot-dialog__close"
            disabled={busy}
            onClick={onClose}
            variant="ghost"
          >
            <span aria-hidden="true">×</span>
          </Button>
        </header>
        {children}
      </section>
    </div>,
    portalTarget,
  );
}
